/* eslint-disable complexity */
/* eslint-disable no-magic-numbers */

import { clamp } from 'lodash';
import { LayoutItem as Layout } from './components/grid-layout';
import { WIDTH_STEPS, pageSizesOptions } from './constants';
import { APP_WIDGET_NAMES, CARD_TYPES, LayoutData, PageSetup, WIDGET_TYPES, WidgetData } from './types';

/**
 * conversion functions between inches,  vertical units and pixels
 */
export const gridVerticalUnitsToPX = (gridUnits: number) => 10 * gridUnits;
export const pXToGridVerticalUnits = (pixels: number) => Math.ceil(pixels / 10);
export const inchesToPX = (inches: number) => (inches * pageSizesOptions[0].dimensions.width) / 8.3;

// returns number of pages in a template based on the positions and heights of items in the layout
export const getNumberOfPagesInTemplate = (templateLayout: Layout[], pageHeight: number) => {
  const maxY = (templateLayout || []).reduce((acc, cur) => (acc < cur.y + cur.h ? cur.y + cur.h : acc), 0);
  return Math.ceil(gridVerticalUnitsToPX(maxY) / pageHeight);
};

/**
 * Returns the source template id only when a report still needs its template layout inserted.
 * Materialized reports retain `sourceTemplateId` for provenance but must not re-enter insertion.
 */
export const getSourceTemplateIdToInsert = (sourceTemplateId: string | undefined, pageLayout: Layout[]) =>
  pageLayout.length === 0 ? sourceTemplateId || '' : '';

// checks if page contains table that goes across multiple pages
export const pageContainsMultiPagesTableFromPrevPages = (
  pageNumber: number,
  pageHeight: number,
  pageLayout: Layout[]
) => {
  const pageStart = pageNumber * pageHeight;
  for (const pl of pageLayout) {
    const widgetYStartPosition = gridVerticalUnitsToPX(pl.y);
    const widgetYEndPosition = widgetYStartPosition + gridVerticalUnitsToPX(pl.h);
    if (
      widgetYStartPosition < pageStart &&
      widgetYEndPosition >= pageStart // widget starts before page and ends in or after page
    ) {
      return true;
    }
  }
  return false;
};

/**
 * get pageHeight in px from page setup object
 */
export const getPageHeightFromSetup = (pageSetup: PageSetup) => {
  const size = pageSizesOptions.find(e => e.value === pageSetup.pageSize) || pageSizesOptions[0];
  const {
    dimensions: { width, height }
  } = size;
  return pageSetup.orientation === 'portrait' ? height : width - 0.4; // adjustments to fix added page when printing
};

/**
 * returns available space above and below an item in the grid layout
 */
export const getAvailableSpace = (item: Layout, pageLayout: Layout[]) => {
  let spaceAbove: number = item.y,
    spaceBelow: number | undefined;
  pageLayout.forEach(e => {
    if ((item.x >= e.x && item.x <= e.x + e.w) || (item.x + item.w >= e.x && item.x + item.w <= e.x + e.w)) {
      if (e.y + e.h < item.y) {
        const space = item.y - (e.y + e.h);
        spaceAbove = spaceAbove === undefined || space < spaceAbove ? space : spaceAbove;
      }
      if (item.y < e.y) {
        const space = e.y - item.y;
        spaceBelow = spaceBelow === undefined || space < spaceBelow ? space : spaceBelow;
      }
    }
  });
  return { spaceAbove, spaceBelow };
};

/**
 * get available space below card until next card or page end
 */
export const getAvailableSpaceBelowCard = (
  e: Layout,
  pageLayout: Layout[],
  stepsPerPage: number,
  limit: number[]
): number => {
  let yEnd = e.y + e.h;
  if (e.y + e.h > limit[0] && e.y + e.h < limit[1]) {
    yEnd = limit[1];
  }
  return pageLayout.reduce(
    (acc, cur) =>
      cur.i !== e.i &&
      ((e.x >= cur.x && e.x < cur.x + cur.w) ||
        (e.x + e.w > cur.x && e.x + e.w <= cur.x + cur.w) ||
        (e.x <= cur.x && e.x + e.w >= cur.x + cur.w)) &&
      cur.y > e.y
        ? acc < cur.y - yEnd || cur.y - yEnd <= 0
          ? acc
          : cur.y - yEnd
        : acc,
    stepsPerPage
  );
};

/**
 * get available space above card until prev card or page end
 */
export const getAvailableSpaceAboveCard = (
  e: Layout,
  pageLayout: Layout[],
  stepsPerPage: number,
  limit: number[]
): number => {
  let yStart = e.y;
  if (e.y > limit[0] && e.y < limit[1]) {
    yStart = limit[0];
  }
  return pageLayout.reduce(
    (acc, cur) =>
      cur.i !== e.i &&
      ((e.x >= cur.x && e.x < cur.x + cur.w) ||
        (e.x + e.w > cur.x && e.x + e.w <= cur.x + cur.w) ||
        (e.x <= cur.x && e.x + e.w >= cur.x + cur.w)) &&
      cur.y + cur.h <= yStart
        ? acc < yStart - cur.y - cur.h
          ? acc
          : yStart - cur.y - cur.h
        : acc,
    stepsPerPage
  );
};

/**
 * returns ids of widgets in specified page number
 */
export const getWidgetIdsByPage = (pageNumber: number, layout: Layout[], pageHeight: number) => {
  const pageStart = pageNumber * pageHeight;
  const pageEnd = (pageNumber + 1) * pageHeight - 1;
  // find all widgets on specific page
  return layout
    .filter(pl => {
      const widgetYStartPosition = gridVerticalUnitsToPX(pl.y);
      const widgetYEndPosition = widgetYStartPosition + gridVerticalUnitsToPX(pl.h);
      if (
        (widgetYStartPosition >= pageStart && widgetYStartPosition < pageEnd) || // widget starts in page
        (widgetYEndPosition > pageStart && widgetYEndPosition <= pageEnd) || // widget ends in page
        (widgetYStartPosition < pageStart && widgetYEndPosition > pageEnd) // widget starts before page and ends after page
      ) {
        return true;
      }
    })
    .map(w => w.i);
};

/**
 * Returns the width step (x-axis section) number of the given position in the given container
 * @param container - the container to measure the position within
 * @param position - the position inside the given container to get the width step (x-axis section) number of
 */
export const getWidthStepFromPosition = (container: HTMLElement, position: number) => {
  const { left, width } = container.getBoundingClientRect();
  const leftGutter = left + window.scrollX;
  const step = Math.round(((position - leftGutter) / width) * WIDTH_STEPS);
  return clamp(step, 0, WIDTH_STEPS);
};

/**
 * Will return the corrected width step (x-axis section) to insert the widget to
 * @param targetStep - The uncorrected width step step (likely the one the user dropped the widget on)
 * @param widgetWidth - The width of the widget in width steps
 */
export const getCorrectedTargetWidthStep = (targetStep: number, widgetWidth: number) => {
  // Keep target step if possible, but ensure widget does not go off the edge of the page
  // if ((targetStep + widgetWidth) > WIDTH_STEPS) {
  //   return clamp(WIDTH_STEPS - widgetWidth, 0, WIDTH_STEPS);
  // }
  // return targetStep;

  // Snap widget left / right
  if (widgetWidth > WIDTH_STEPS / 2) {
    // if widget is more than 50% wide, snap left
    return 0;
  } else if (targetStep < WIDTH_STEPS / 2) {
    // if widget dropped in left 50% of page, snap left
    return 0;
  } else {
    // else snap right
    return clamp(WIDTH_STEPS - widgetWidth, 0, WIDTH_STEPS);
  }
};

/**
 * Will return cleaned up new layout where it shrinks any widget that is longer than 1 page (unless it's a table or customers & suppliers widget)
 * and moves any widget that starts in / ends in or contains header or footer area
 * @param layout: layout to clean up
 * @param layoutData: layout data
 * @param stepsPerPage: number of steps per page
 * @param headerFooterLimits: limits of header and footer (area that should be clean from widgets that do not span between pages)
 * @returns
 */
export const cleanUpLayout = (
  layout: Layout[],
  layoutData: LayoutData[],
  stepsPerPage: number,
  headerFooterLimits: number[][]
): Layout[] => {
  let shouldUpdate = false;
  const newLayout = layout
    .map(e => {
      const cardData = layoutData.find(ld => ld.i === e.i);
      const widgetOptions = cardData ? (cardData[CARD_TYPES.WIDGET] as WidgetData) : null;
      const canSpanAcrossPages =
        widgetOptions?.widgetType === WIDGET_TYPES.TABLE ||
        cardData?.[CARD_TYPES.APP_WIDGET]?.name === APP_WIDGET_NAMES.CUSTOMERS_AND_SUPPLIERS;
      let newE = { ...e };
      // resize any element that is taller than 1 page - margins except for tables
      if (e.h > stepsPerPage && !canSpanAcrossPages) {
        newE = { ...newE, h: stepsPerPage };
        shouldUpdate = true;
        return newE;
      }
      // if e crosses starts in header + footer area
      for (const limit of headerFooterLimits) {
        if (
          (e.y >= limit[0] && e.y < limit[1]) || // scenario 1: card starts in disallowed area
          (e.y + e.h > limit[0] && e.y + e.h <= limit[1] && !canSpanAcrossPages) || // scenario 2: card ends in disallowed area
          (e.y <= limit[0] && e.y + e.h >= limit[1] && !canSpanAcrossPages) // scenario 3: card contains disallowed area
        ) {
          const amountDown = limit[1] - e.y; // amount needed to move card up enough to avoid disallowed area
          const amountUp = e.y - limit[0] + e.h; // amount needed to move card down enough to avoid disallowed area

          const spaceDown = getAvailableSpaceBelowCard(e, layout, stepsPerPage, limit);
          const spaceUp = getAvailableSpaceAboveCard(e, layout, stepsPerPage, limit);

          // we move to the closest available space, if not enough available space, we also resize
          if (amountUp <= amountDown && amountUp > 0) {
            if (amountUp <= spaceUp) {
              newE = { ...newE, y: e.y - amountUp };
            } else if (amountDown <= spaceDown) {
              newE = { ...newE, y: e.y + amountDown };
            } else if (e.h + spaceUp - amountUp > 0) {
              // resize
              newE = { ...newE, h: e.h + spaceUp - amountUp, y: e.y - amountUp };
            }
          } else {
            if (amountDown <= spaceDown) {
              newE = { ...newE, y: e.y + amountDown };
            } else if (amountUp <= spaceUp) {
              newE = { ...newE, y: e.y - amountUp };
            } else if (e.h + spaceDown - amountDown > 0) {
              // resize
              newE = { ...newE, h: e.h + spaceDown - amountDown, y: e.y + amountDown };
            }
          }
          // we delete any widgets with negative hight that might have happened in the old code
          if (newE.h < 0) {
            newE = { ...newE, h: 0 };
          }
          // if issue with a widget placement could not be solved, we skip updating to avoid infinite calls
          if (
            !(
              (newE.y >= limit[0] && newE.y < limit[1]) ||
              (newE.y + newE.h > limit[0] && newE.y + newE.h <= limit[1] && !canSpanAcrossPages) ||
              (newE.y <= limit[0] && newE.y + newE.h >= limit[1] && !canSpanAcrossPages)
            )
          ) {
            shouldUpdate = true;
          }
        }
      }

      return shouldUpdate ? newE : e;
    })
    .filter(e => e.h !== 0);

  if (shouldUpdate) {
    return cleanUpLayout(newLayout, layoutData, stepsPerPage, headerFooterLimits);
  } else {
    return layout;
  }
};

/**
 * Get layout border style based on page setup and edit/print mode
 * @param showMargins - from page setup
 * @param isEditable - is report in edit mode
 * @param printState - is report in print mode
 * @returns border style
 */
export const getLayoutBorder = (showMargins: boolean, isEditable?: boolean, printState?: boolean) =>
  showMargins && isEditable && !printState ? '1px dashed #C6C6C6' : 'none';
